import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { ErrorBoundary } from '@/components/error/ErrorBoundary';

/**
 * Core-100 R4 — CORE-AE GAP-004 (tab failure containment).
 *
 * The Command Center composes sections owned by other capabilities. Before R4 a section that threw while rendering
 * took the WHOLE ticket view down with it (no boundary around any tab). Each section now fails on its own: it says
 * that IT could not be shown — a failure, not an empty section — and every other section keeps working.
 * Keyed by the ticket, so opening another ticket retries.
 */
export function CommandCenterTabBoundary({ section, ticketId, children }: { section: string; ticketId: string; children: ReactNode }) {
  return (
    <ErrorBoundary
      key={`${section}:${ticketId}`}
      fallback={
        <div role="alert" className="m-6 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm" data-testid={`command-center-section-failed-${section}`}>
          <AlertTriangle className="h-4 w-4 mt-0.5 text-destructive" />
          <div>
            <p className="font-medium">{section} could not be shown.</p>
            <p className="text-muted-foreground">This is an error, not an empty section. The other sections are unaffected.</p>
          </div>
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
}
